import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GlobalConstants } from '@globalConstants';
import { ICourseFormErrors } from '@interfaces/ICourseFormErrors';
import { IGroup } from '@interfaces/IGroup';
import { IAppState } from '@myStore/app.state';
import {
  ClearGroups,
  GetGroupsRequest,
} from '@myStore/groups-view/groups-view.actions';
import {
  selectGroups,
  selectLoadingGroupsList,
  selectLoadingGroupsListErrors,
} from '@myStore/groups-view/groups-view.selector';
import {
  AddHolidaysToSeveralGroupsRequest,
  ClearAddHolidaysToSeveralGroups,
} from '@myStore/periods-changes/periods-changes.actions';
import {
  selectPeriodChangesLoading,
  selectPeriodChangesSuccess,
} from '@myStore/periods-changes/periods-changes.selector';
import { select, Store } from '@ngrx/store';
import { CustomDateAdapter } from '@shared/adapter/date.adapter';
import { FormDialogComponent } from '@shared/components/UI/form-dialog/form-dialog.component';
import { formatDatePicker } from '@shared/utils/dateUtils';
import { isControlsIsValid } from '@shared/utils/formUtils';
import { MyErrorStateMatcher } from '@validators/matcher';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-add-holidays-with-groups-select-form',
  templateUrl: './add-holidays-with-groups-select-form.component.html',
  styleUrls: ['./add-holidays-with-groups-select-form.component.sass'],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: GlobalConstants.DEFAULT_DATEPICKER_FORMAT,
    },
  ],
})
export class AddHolidaysWithGroupsSelectFormComponent
  implements OnInit, OnDestroy
{
  private destroy$: Subject<boolean> = new Subject<boolean>();

  public requiredErrorMessage = 'Поле является обазательным';
  public holidayForm = new UntypedFormGroup({
    offset_date: new UntypedFormControl(GlobalConstants.dateFormat, [
      Validators.required,
    ]),
    weeks_offset: new UntypedFormControl(1, [Validators.required]),
    groups: new UntypedFormControl([], [Validators.required]),
  });
  public matcher = new MyErrorStateMatcher();
  public coursesLoading$ = this._store.select(selectLoadingGroupsList);
  public sendLoading$ = this._store.pipe(select(selectPeriodChangesLoading));

  errors$: ICourseFormErrors | null = null;
  public selectedGroups = new Set<number>();
  public groups$: IGroup[] = [];
  public allSelected = false;

  constructor(
    private _store: Store<IAppState>,
    private _dialog: MatDialog,
    private _dialogRef: MatDialogRef<FormDialogComponent>
  ) {}

  ngOnInit(): void {
    this.selectedGroups.clear();
    this._store.dispatch(new GetGroupsRequest({ is_active: 'true' }));

    this._store
      .pipe(select(selectLoadingGroupsListErrors), takeUntil(this.destroy$))
      .subscribe(errors => {
        this.errors$ = errors;
        this.setControlErrors(errors);
      });

    this._store
      .pipe(select(selectGroups), takeUntil(this.destroy$))
      .subscribe(groups => {
        if (groups) {
          this.groups$ = groups.results;
        }
      });

    this._store
      .pipe(select(selectPeriodChangesSuccess), takeUntil(this.destroy$))
      .subscribe(success => {
        if (success) {
          this.onCancel();
          this._store.dispatch(new ClearGroups());
          this._store.dispatch(new ClearAddHolidaysToSeveralGroups());
          this.selectedGroups.clear();
        }
      });
  }

  setControlErrors(errors: ICourseFormErrors | null): void {
    if (errors) {
      Object.keys(errors).forEach(key => {
        if (this.holidayForm.controls[key]) {
          this.holidayForm.controls[key].setErrors({ errors: errors[key] });
        }
      });
    }
  }

  onSubmit() {
    if (this.selectedGroups.size > 0) {
      this.holidayForm.patchValue({ groups: [...this.selectedGroups] });
    }

    const formData = this.holidayForm.value;
    let offsetDate = formData.offset_date;

    if (offsetDate && offsetDate._i) {
      offsetDate = formatDatePicker(offsetDate._i);
    }

    if (isControlsIsValid(this.holidayForm.controls)) {
      const holidayData = {
        ...formData,
        offset_date: offsetDate,
      };

      this._store.dispatch(new AddHolidaysToSeveralGroupsRequest(holidayData));
    }
  }

  onCancel() {
    this.selectedGroups.clear();
    this._dialogRef.close();
  }

  get offsetDate() {
    return this.holidayForm.get('offset_date');
  }

  get offsetWeeks() {
    return this.holidayForm.get('weeks_offset');
  }

  getMessageFormError(name: string): string | null {
    if (this.errors$ && this.errors$[name]) {
      return this.errors$[name][0];
    }

    return null;
  }

  toggleSelectGroups(id: number) {
    this.selectedGroups.has(id)
      ? this.selectedGroups.delete(id)
      : this.selectedGroups.add(id);
  }

  isSelectGroup(id: number): boolean {
    return this.selectedGroups.has(id);
  }

  someSelected(): boolean {
    return (
      this.groups$.filter(group => {
        this.selectedGroups.has(group.id);
      }).length > 0 && !this.allSelected
    );
  }

  updateAllSelected() {
    this.allSelected = this.groups$.every(group => {
      this.selectedGroups.has(group.id);
    });

    return this.allSelected;
  }

  toggleAllSelectGroups(e: MatCheckboxChange) {
    this.allSelected = e.checked;

    if (e.checked) {
      this.groups$.forEach(group => {
        this.selectedGroups.add(group.id);
      });
    } else {
      this.groups$.forEach(group => {
        this.selectedGroups.delete(group.id);
      });
    }
  }

  ngOnDestroy(): void {
    this._store.dispatch(new ClearGroups());
    this._store.dispatch(new ClearAddHolidaysToSeveralGroups());
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
